import React, { useContext, useState } from 'react'
import { useSessionStorage } from '@hooks'
import type { LucaViewDescriptor } from '@containers/AIWidgetEmbed/pageContext'

/** Page-owned Luca grounding (merged over route-derived pageContext entity fields). */
export type LucaViewContext = LucaViewDescriptor

interface Props {
  isInitialized: boolean
  setInitialized: (value: boolean) => void
  isAIHelpOpen: boolean
  setAIHelpOpen: (value: boolean) => void
  allBusinesses: boolean
  setAllBusinesses: (value: boolean) => void
  pendingConversationId: string | null
  setPendingConversationId: (value: string | null) => void
  /** Prompt queued for the embedded widget (sent via AI_WIDGET_SEND_PROMPT). */
  pendingLucaPrompt: string | null
  setPendingLucaPrompt: (value: string | null) => void
  /** Active page descriptor for Luca page_context entity_* fields. */
  viewContext: LucaViewContext | null
  setViewContext: (value: LucaViewContext | null) => void
  widgetBusinessSlugOverride: string | null
  setWidgetBusinessSlugOverride: (value: string | null) => void
}

export const AIHelpContext = React.createContext<Props>(null)

export const useAIHelpContext = (): Props => {
  return useContext(AIHelpContext)
}

interface AIHelpProviderProps {
  children: React.ReactNode
}

export const AIHelpProvider: React.FC<AIHelpProviderProps> = ({ children }) => {
  const [isAIHelpOpen, setAIHelpOpen] = useSessionStorage<boolean>('aiHelp.isOpen', false)
  const [isInitialized, setInitialized] = useState(false)
  const [allBusinesses, setAllBusinesses] = useState(false)
  const [pendingConversationId, setPendingConversationId] = useState<string | null>(null)
  const [pendingLucaPrompt, setPendingLucaPrompt] = useState<string | null>(null)
  const [viewContext, setViewContext] = useState<LucaViewContext | null>(null)
  const [widgetBusinessSlugOverride, setWidgetBusinessSlugOverride] = useState<string | null>(null)

  return (
    <AIHelpContext.Provider
      value={{
        isAIHelpOpen,
        setAIHelpOpen,
        isInitialized,
        setInitialized,
        allBusinesses,
        setAllBusinesses,
        pendingConversationId,
        setPendingConversationId,
        pendingLucaPrompt,
        setPendingLucaPrompt,
        viewContext,
        setViewContext,
        widgetBusinessSlugOverride,
        setWidgetBusinessSlugOverride
      }}
    >
      {children}
    </AIHelpContext.Provider>
  )
}
